import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ExternalLink, Calendar, ArrowRight } from 'lucide-react';
import { mediaAPI } from '../lib/api';
import { convertGoogleDriveUrl } from '../lib/imageUtils';

const PLACEHOLDER_IMAGE = "https://images.unsplash.com/photo-1705291490318-6cc8cb88a1f3?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Media() {
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const response = await mediaAPI.getAll();
        setMediaItems(response.data);
      } catch (error) {
        console.error('Failed to fetch media:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMedia();
  }, []);

  return (
    <div className="paper-texture min-h-screen" data-testid="media-page">
      {/* Header */}
      <section className="py-16 md:py-24 bg-primary">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-accent font-medium text-sm uppercase tracking-widest">Recognition</span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary-foreground mt-4 mb-6">
              Media Coverage
            </h1>
            <p className="text-lg text-primary-foreground/80">
              News articles, features, and recognition of Jabalipuraan's heritage documentation work.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Media List */}
      <section className="py-12 md:py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse flex gap-4">
                  <div className="bg-muted h-24 w-24 flex-shrink-0"></div>
                  <div className="flex-1">
                    <div className="bg-muted h-4 w-1/4 mb-2"></div>
                    <div className="bg-muted h-6 w-3/4 mb-2"></div>
                    <div className="bg-muted h-4 w-full"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : mediaItems.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-8">
              {mediaItems.map((item, idx) => (
                <motion.article
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="group bg-background border border-border hover:border-accent/50 transition-colors duration-300"
                >
                  <Link 
                    to={`/media/${item.id}`}
                    className="flex gap-4 p-4"
                    data-testid={`media-${item.id}`}
                  >
                    <div className="img-zoom w-24 h-24 flex-shrink-0 border border-border">
                      <img 
                        src={convertGoogleDriveUrl(item.image) || PLACEHOLDER_IMAGE}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                        <span className="font-medium text-accent">{item.source}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(item.date).toLocaleDateString()}
                        </span>
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-foreground mb-1 group-hover:text-accent transition-colors duration-300 line-clamp-2">
                        {item.title}
                      </h3>
                      {item.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {item.description}
                        </p>
                      )}
                      <span className="inline-flex items-center gap-1 text-xs text-accent mt-2">
                        Read More <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <h3 className="font-heading text-2xl text-foreground mb-4">No Media Coverage Yet</h3>
              <p className="text-muted-foreground">
                Media mentions and features will appear here.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Press Contact */}
      <section className="py-12 bg-muted/30 border-t border-border">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
            Media Inquiries
          </h3>
          <p className="text-muted-foreground">
            For press inquiries or media coverage, please use our{' '}
            <a href="/contact" className="text-accent hover:underline">contact form</a>.
          </p>
        </div>
      </section>
    </div>
  );
}
